import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The two `aria-live` regions the portal announces through.
 *
 * Authority: bead scope-9t5 (accessibility-specialist required mitigation).
 *
 * There are exactly two regions, not one per caller: `scope-panel` for the
 * questionnaire's own deltas ("3 new questions added") and `upload-status`
 * for per-field file-upload progress. A screen reader user tolerates a
 * handful of live regions on a page; a form that mints one per component
 * would talk over itself. Anything announced goes through `useAnnounce` for
 * one of these two regions — there is no API for creating a third.
 *
 * `useAnnounce` owns three things a plain `aria-live` div does not give you:
 *
 *   - Debounce: a burst of calls (e.g. the classifier re-evaluating on every
 *     keystroke) collapses to one announcement after `settleMs` of quiet.
 *   - Batching: messages queued during that quiet window are joined into one
 *     utterance instead of clobbering each other.
 *   - Re-announcement: screen readers generally do not re-announce a live
 *     region whose text does not change. The region is cleared one tick
 *     before the batched text is set so two identical settles both speak.
 *
 * What a caller announces is its own decision — this module does not decide
 * that a section appearing is "structural, not an announcement" for you. See
 * bead scope-9t5's acceptance notes for that rule; it lives in the caller
 * (the section-visibility engine), not here.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
const REGIONS = ['scope-panel', 'upload-status'];
const DEFAULT_SETTLE_MS = 500;
/** Standard visually-hidden-but-announced technique; not `display: none`, which screen readers skip. */
const VISUALLY_HIDDEN = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};
const IDLE_REGION = { text: '', politeness: 'polite' };
const AnnounceContext = createContext(null);
function emptyTimerMap() {
    return { 'scope-panel': null, 'upload-status': null };
}
function clearAll(timers) {
    for (const region of REGIONS) {
        const timer = timers[region];
        if (timer !== null)
            clearTimeout(timer);
    }
}
/** Clears the region, then (one tick later) sets the batched text — see the module doc for why. */
function flushRegion(refs, region, politeness, setRegions) {
    refs.settleTimer[region] = null;
    const batched = refs.pending[region].join('. ');
    refs.pending[region] = [];
    const existingFlip = refs.flipTimer[region];
    if (existingFlip !== null)
        clearTimeout(existingFlip);
    setRegions((current) => ({ ...current, [region]: { text: '', politeness } }));
    refs.flipTimer[region] = setTimeout(() => {
        refs.flipTimer[region] = null;
        setRegions((current) => ({ ...current, [region]: { text: batched, politeness } }));
    }, 0);
}
/** Debounce, batch, and clear-then-set state for both regions; the component below only renders it. */
function useRegionAnnouncer(settleMs) {
    const [regions, setRegions] = useState({
        'scope-panel': IDLE_REGION,
        'upload-status': IDLE_REGION,
    });
    const refs = useRef({
        pending: { 'scope-panel': [], 'upload-status': [] },
        settleTimer: emptyTimerMap(),
        flipTimer: emptyTimerMap(),
    });
    useEffect(() => () => {
        clearAll(refs.current.settleTimer);
        clearAll(refs.current.flipTimer);
    }, []);
    const announce = useCallback((region, message, options) => {
        const trimmed = message.trim();
        if (trimmed === '')
            return;
        const queue = refs.current.pending[region];
        if (queue[queue.length - 1] !== trimmed)
            queue.push(trimmed);
        const politeness = options?.politeness ?? 'polite';
        const existingSettle = refs.current.settleTimer[region];
        if (existingSettle !== null)
            clearTimeout(existingSettle);
        refs.current.settleTimer[region] = setTimeout(() => {
            flushRegion(refs.current, region, politeness, setRegions);
        }, settleMs);
    }, [settleMs]);
    return { regions, announce };
}
export function LiveRegionManager({ settleMs = DEFAULT_SETTLE_MS, children, }) {
    const { regions, announce } = useRegionAnnouncer(settleMs);
    const value = useMemo(() => ({ announce }), [announce]);
    return (_jsxs(AnnounceContext.Provider, { value: value, children: [children, REGIONS.map((region) => (_jsx("div", { "data-live-region": region, role: "status", "aria-live": regions[region].politeness, "aria-atomic": "true", style: VISUALLY_HIDDEN, children: regions[region].text }, region)))] }));
}
/**
 * Returns a stable `announce` callback bound to one region. Must be called
 * beneath a `LiveRegionManager` — there is no ungoverned fallback, because an
 * announcement with nowhere to land is a silent no-op that looks like a bug
 * report waiting to happen.
 */
export function useAnnounce(region) {
    const ctx = useContext(AnnounceContext);
    if (ctx === null) {
        throw new Error('useAnnounce must be called beneath a <LiveRegionManager>');
    }
    const { announce } = ctx;
    return useCallback((message, options) => announce(region, message, options), [announce, region]);
}
//# sourceMappingURL=LiveRegionManager.js.map